///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import {
    ActionsToolbarModeEnum,
    ActiveRestorableWindowConfigDTO,
    FeatureTypeEnum,
    GtthStepGroupEnum,
    MapTypeEnum,
    PositionDTO,
    ProjectSelectionDTO,
    ProjectUserSessionDTO
} from '../../../generated/shared-types.model';
import { ActiveRestorableWindow } from '../../project-windows/active-restorable-window';
import { AbstractInteractiveWindow } from '../../project-windows/abstract-interactive-window';
import { GtthPosition } from '../entities/position';
import { GtthHistoryService } from './history-service';

@Injectable({
    providedIn: 'root'
})
export class GtthProjectUserSessionService {
    private openForModifications = false;
    private sessionInfoChanged = false;
    private userSession: ProjectUserSessionDTO;

    get isAutoRepeatDesign() {
        return this.userSession ? this.userSession.autoRepeatDesign : true;
    }

    set isAutoRepeatDesign(value) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.autoRepeatDesign = value;
        }
    }

    get isShowLinksAndVerifiedLoses(): boolean {
        return this.userSession ? this.userSession.showLinksAndVerifiedLoses : true;
    }

    set isShowLinksAndVerifiedLoses(value: boolean) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.showLinksAndVerifiedLoses = value;
        }
    }

    get isShowSectors(): boolean {
        return this.userSession ? this.userSession.showSectors : true;
    }

    set isShowSectors(value: boolean) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.showSectors = value;
        }
    }

    get isShowAllSectors(): boolean {
        return this.userSession ? this.userSession.showAllSectors : true;
    }

    set isShowAllSectors(value: boolean) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.showAllSectors = value;
        }
    }

    get isUseEngineColors(): boolean {
        return this.userSession ? this.userSession.useEngineColors : false;
    }

    set isUseEngineColors(value: boolean) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.useEngineColors = value;
        }
    }

    public loadSessionObject(session: ProjectUserSessionDTO): void {
        this.userSession = session;
    }

    refreshSelectionAndOpenWindows(historyService: GtthHistoryService): void {
        historyService.interactiveWindowsService.storeActiveWindows();
        this.storeSelection(historyService.logicalTopology.getSelectedEntities());
        this.storeSelectArea(historyService.selectAreaService.getSelectedAreaPath());
    }

    openForModification() {
        this.openForModifications = true;
    }

    closeForModifications() {
        this.openForModifications = false;
    }

    clear() {
        this.userSession = undefined;
    }

    getSessionObject() {
        return this.userSession;
    }

    getSessionObjectIfChanged() {
        if (this.sessionInfoChanged && this.userSession) {
            this.sessionInfoChanged = false;
            return this.userSession;
        } else {
            return undefined;
        }
    }

    public applyMapCoords(latLng: any, zoomLevel: number): void {
        if (this.openForModifications) {
            this.userSession.bbox = [latLng.lng(), latLng.lat(), zoomLevel];
        }
    }

    setMapType(mapTypeId: MapTypeEnum) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.mapType = mapTypeId;
        }
    }

    setStepGroup(selectedStep: GtthStepGroupEnum) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.stepGroupNumber = selectedStep;
        }
    }

    setActionMode(actionMode: ActionsToolbarModeEnum) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.actionMode = actionMode;
        }
    }

    setShowPolygons(isShowStoredPolygons: boolean) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.showStoredPolygons = isShowStoredPolygons;
        }
    }

    storeActiveModelWindow(utlRequest: string, window: ActiveRestorableWindow): void {
        if (this.openForModifications && window.isRestorable()) {
            this.sessionInfoChanged = true;
            this.userSession.activeModalDialog = {
                request: utlRequest,
                designRequired: window.designRequired(),
                bbox: []
            } as ActiveRestorableWindowConfigDTO;
        }
    }

    clearActiveModelWindow() {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.activeModalDialog = undefined;
        }
    }

    setShowDesignIterations(isShow: boolean) {
        if (this.openForModifications && this.userSession.showDesignIterations !== isShow) {
            this.sessionInfoChanged = true;
            this.userSession.showDesignIterations = isShow;
        }
    }

    storeInteractiveDialog(routerPath: string, component: AbstractInteractiveWindow) {
        if (this.openForModifications && component.isRestorable()) {
            this.sessionInfoChanged = true;
            this.userSession.activeInteractiveDialog.push({
                request: routerPath,
                designRequired: component.designRequired(),
                bbox: component.getBbox()
            });
        }
    }

    clearInteractiveDialogs() {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.activeInteractiveDialog = [];
        }
    }

    storeSelection(selectedEntities: ProjectSelectionDTO) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.selectedEntities = selectedEntities;
        }
    }

    clearSelection() {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.selectedEntities = {
                selectedNodes: [],
                selectedLinkIds: [],
                selectedSectors: []
            };
        }
    }

    storeSelectArea(activeSelectArea: GtthPosition[]) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;

            if (activeSelectArea?.length) {
                this.userSession.activeSelectArea = activeSelectArea.map((point) => {
                    return {
                        latitude: point.latitude,
                        longitude: point.longitude
                    } as PositionDTO;
                });
            } else {
                this.userSession.activeSelectArea = [];
            }
        }
    }

    saveInfoWindowConfig(entityId: number, position: GtthPosition, featureType: FeatureTypeEnum) {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.infoWindowConfig = {
                type: featureType,
                position: {
                    latitude: position.latitude,
                    longitude: position.longitude
                } as PositionDTO,
                entityId
            };
        }
    }

    clearInfoWindow() {
        if (this.openForModifications) {
            this.sessionInfoChanged = true;
            this.userSession.infoWindowConfig = null;
        }
    }
}
